import {CircularProgress, Box} from '@material-ui/core';
import React from 'react';
import {connect} from 'react-redux';
import downloadCSVIcon from '../../../../assets/CSV.png';
import {getDevicesCSV} from '../../../../state/ducks/devices/operations';
import {DeviceApp} from '../../../../state/ducks/devices/types';
import {localized} from '../../../../state/i18n/i18n';
import {AppState} from '../../../../state/store';
import {GetColorByIndex} from '../../../../utilities/colors';
import {DownloadButton} from '../../default-components/download-button/download-button';
import {GraphLegend} from '../../default-components/graph-legend/graph-legend';
import {RadioButtonGroup} from '../../default-components/radio-button-group/radio-button-group';
import './device-graph.scss';
import {Dispatch} from 'redux';
import {getPrecipPast1h} from '../../../../state/ducks/weather-data-dmi/operations';
import {LineData, LineChart} from '../../default-components/line-chart/line-chart';
import isEqual from 'react-fast-compare';

const mapStateToProps = (state: AppState) => {
  return {
    devices: state.devicesReducer.devices,
    weatherData: state.weatherReducer.stationObservations,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  getWeather: async (stationId: string, to: Date, from: Date) => (await getPrecipPast1h(stationId, to, from))(dispatch),
});

interface PropsFromParent {
  dateFrom: Date;
  dateTo: Date;
}

type Props = ReturnType<typeof mapStateToProps> & ReturnType<typeof mapDispatchToProps> & PropsFromParent;

interface State {
  selected: string;
  downloading: boolean;
  availableTypes: string[];
}

interface StationIdRainData {
  stationId: string;
  rainData: {t: Date; y: number}[];
}

class DeviceGraph extends React.PureComponent<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      selected: localized('Nitrate'),
      downloading: false,
      availableTypes: [localized('Nitrate'), localized('Temperature')],
    };
  }

  public componentDidMount = () => {
    this.getWeather();
    this.generateWeatherData();
  };

  public componentDidUpdate = (prevProps: Props) => {
    let newUniqueStations = this.getUniqueStations(this.props);
    let oldUniqueStations = this.getUniqueStations(prevProps);

    if (
      !isEqual(oldUniqueStations, newUniqueStations) ||
      !isEqual(prevProps.dateFrom, this.props.dateFrom) ||
      !isEqual(prevProps.dateTo, this.props.dateTo)
    ) {
      this.getWeather();
    }
  };

  public render() {
    return <div>{this.GetDeviceGraphContent()}</div>;
  }

  private getUniqueStations(props: Props) {
    let stations = props.devices.filter((device) => device.stationId).map((device) => device.stationId!);
    let uniqueStations = [...new Set(stations)];

    return uniqueStations;
  }

  private getWeather = () => {
    let uniqueStations = this.getUniqueStations(this.props);
    uniqueStations.forEach((stationId) => this.props.getWeather(stationId, this.props.dateTo, this.props.dateFrom));
  };

  private generateWeatherData() {
    let stationDataList: StationIdRainData[] = [];
    let stationData: StationIdRainData;

    this.props.devices.forEach((element) => {
      if (stationDataList.find((data) => data.stationId === element.stationId) === undefined) {
        if (element.stationId && this.props.dateTo && this.props.dateFrom) {
          let data: {t: Date; y: number}[];

          data = this.precipitaionDataIntoBars(element.stationId);
          stationData = {rainData: data, stationId: element.stationId};
          stationDataList.push(stationData);
        }
      }
    });

    return stationDataList;
  }

  private downloadCSV = async () => {
    this.setState({downloading: true});
    const deviceIds = this.props.devices.filter((a) => a.selected).map((a) => a.deviceId!);

    try {
      await getDevicesCSV(deviceIds, this.props.dateFrom, this.props.dateTo);
    } finally {
      this.setState({downloading: false});
    }
  };

  private GetDeviceTopHeaderApp = () => (
    <div className="flexWrap padding">
      <div className="flexDirectionRow appButtonTypes">{this.GetRadioButtonGroup()}</div>
      <div className="flexDirectionRow flexWrap deviceLegend">{this.mapDeviceLegends()}</div>
    </div>
  );

  private GetRadioButtonGroup = () => (
    <RadioButtonGroup
      defaultValue={localized('Nitrate')}
      onChange={this.setSelected}
      values={this.state.availableTypes}
    />
  );

  private GetDeviceTopHeaderWeb = () => (
    <div className="flexDirectionRow alignItemsCenter flexWrap legendsContainer ">
      <Box flexDirection="row" display="flex" className="legendsTab">
        <Box>{this.GetRadioButtonGroup()}</Box>
        <Box className="deviceLegend" flexDirection="row">
          {this.mapDeviceLegends()}
        </Box>
        <Box>
          <DownloadButton
            icon={
              <div className="paddingRight">
                <div>{this.state.downloading && <CircularProgress className="spinnerGraph" />}</div>
                <img
                  src={downloadCSVIcon}
                  className={
                    'downloadCSVIcon ' +
                    (this.state.downloading ? localized('Downloading') : localized('NotDownloading'))
                  }
                  alt="downloadCSVIcon"
                />
              </div>
            }
            text={'Download CSV'}
            onClick={this.downloadCSV}
            className="downloadCSV"
          />
        </Box>
      </Box>
    </div>
  );

  private GetDeviceGraphContent = () => (
    <div>
      <div className="desktop graphWidth">{this.GetDeviceTopHeaderWeb()}</div>
      <div className="mobile">{this.GetDeviceTopHeaderApp()}</div>
      <div>{this.getLineChart()}</div>
    </div>
  );

  private setSelected = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e && e.target && e.target.value) {
      this.setState({selected: e.target.value});
    }
  };

  private getLineChart = () => {
    const stationData = this.generateWeatherData();
    let lineDatas: LineData[] = [];

    this.props.devices.forEach((device, index) => {
      let lineData = this.mapDevices(device, index, stationData);
      if (lineData) {
        lineDatas.push(lineData);
      }
    });

    return (
      <LineChart
        scaleLabelY={
          this.state.selected === localized('Nitrate') ? localized('NitrateValue') : localized('TemperatureValue')
        }
        scaleY={this.state.selected === localized('Nitrate') ? localized('mgSlashl') : localized('celsius')}
        scaleLabelRainY={localized('Rain')}
        scaleYRain={localized('mm')}
        scaleLabelX={localized('Date')}
        suggestedMinYValue={0}
        suggestedMaxYValue={10}
        suggestedMaxYRainValue={5}
        minDate={this.props.dateFrom}
        maxDate={this.props.dateTo}
        data={lineDatas}
      />
    );
  };

  private mapDeviceLegends = () => {
    let graphLegends = this.props.devices.map((d: DeviceApp, index: number) => (
      <GraphLegend key={d.deviceId} text={d.deviceId ? d.deviceId : ''} color={GetColorByIndex(index)} />
    ));

    return graphLegends;
  };

  private precipitaionDataIntoBars = (stationId: string) => {
    let allObservation = this.props.weatherData;
    let rainDataForStation: {t: Date; y: number}[] = [];

    if (allObservation) {
      let stationObservations = allObservation.find((observation) => observation.stationId === stationId);
      if (stationObservations?.observations.length) {
        let chunkSize = 6;

        for (let i = 0; i < stationObservations.observations.length - 5; i += chunkSize) {
          let chunk = stationObservations.observations.slice(i, i + chunkSize);

          let sumOfPrecipitation = chunk.reduce((sum, currentValue) => {
            return sum + currentValue.properties.value;
          }, 0);

          let dateOfMeasurement = stationObservations.observations[i].properties.observed;

          if (dateOfMeasurement && sumOfPrecipitation) {
            rainDataForStation.push({t: dateOfMeasurement, y: sumOfPrecipitation});
          }
        }
      }
    }
    return rainDataForStation;
  };

  private mapDevices = (
    d: DeviceApp,
    index: number,
    stationData: StationIdRainData[] | undefined,
  ): LineData | undefined => {
    if (
      d.selected &&
      (this.state.selected === localized('Temperature') || this.state.selected === localized('Nitrate'))
    ) {
      const lineData: LineData = {
        colorTheme: GetColorByIndex(index),
        name: d.deviceId,
        data: d
          .data!.filter((a) => a.measurementTime >= this.props.dateFrom && a.measurementTime <= this.props.dateTo)
          .sort((a, b) => {
            return a.measurementTime.getTime() - b.measurementTime.getTime();
          })
          .map((a) => {
            return {
              t: a.measurementTime,
              y: this.state.selected === localized('Nitrate') ? a.correctedNitrateConcentration : a.sensorTemperature,
            } as {t?: Date; x?: number; y: number};
          }),
      };

      if (d.stationId) {
        let dataFromState = stationData?.find((sd) => sd.stationId === d.stationId);
        if (dataFromState?.rainData.length) {
          lineData.rainData = dataFromState.rainData;
        }
      }
      return lineData;
    }

    return;
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(DeviceGraph);
